import { useState, useEffect, useRef } from "react"
import { ArrowLeft, Mic, MicOff, Video, VideoOff, Phone, Loader2, Volume2, VolumeX } from "lucide-react"
import { Button } from "@/clientWeb/components/ui/button"
import { useTheme } from "@/clientWeb/contexts/ThemeContext"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"

interface WaitingLobbyProps {
  onBack: () => void
  userName?: string
  meetingCode?: string
}

export function WaitingLobby({ onBack, userName = "Guest" }: WaitingLobbyProps) {
  const t = useTranslation();
  const { theme } = useTheme()
  const [micEnabled, setMicEnabled] = useState(true)
  const [videoEnabled, setVideoEnabled] = useState(true)
  const [, setDots] = useState("")
  const [videoMuted, setVideoMuted] = useState(true)
  const videoRef = useRef<HTMLVideoElement>(null)

  // Animated dots for waiting message
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? "" : prev + ".")
    }, 500)
    return () => clearInterval(interval)
  }, [])

  // Initialize video
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = true
      videoRef.current.loop = true
      videoRef.current.play().catch(() => {
        console.log("Auto-play prevented, user interaction required")
      })
    }
  }, [])

  // Get user initials
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <div className="h-[100dvh] flex bg-background text-foreground flex flex-col">
      {/* Header */}
      <header className="sticky top-0 bg-card/80 backdrop-blur-sm border-b border-border">
        <div className="px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
           <div className="w-24">
          <img src={theme === 'dark' ? '/InmeetWhiteLogo.svg' : '/Inmeet.svg'} alt=""/>
        </div>
          <Button
            onClick={onBack}
            variant="ghost"
            className="group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">{t("waitingLobby.backToDashboard")}</span>
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 w-full sm:px-6 lg:px-4 py-4 sm:py-4 pb-2 flex flex-1 overflow-y-auto">
        <div className="grid lg:grid-cols-[1.2fr_0.8fr] w-full gap-8 lg:gap-12 justify-center items-center h-full">
          {/* Left: Video Preview */}
          <div className="space-y-4 pl-0 sm:pl-10">
            {/* Video Preview */}
            <div className="bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 rounded-2xl shadow-2xl overflow-hidden aspect-video relative">
              {/* Video Player */}
              <video
                ref={videoRef}
                className="absolute inset-0 w-full h-full object-cover"
                playsInline
                muted
                loop
              >
                <source src="https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4" type="video/mp4" />
                <source src="https://www.w3schools.com/html/mov_bbb.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>

              {/* Video overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
              
              {/* Decorative elements */}
              <div className="absolute top-10 left-10 w-20 h-20 bg-indigo-500/10 rounded-full blur-2xl animate-pulse"></div>
              <div className="absolute bottom-10 right-10 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
              
              {/* Video controls overlay */}
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                <div className="flex items-center gap-2 text-white/80 text-sm">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span>{t("waitingLobby.livePreview")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => {
                      if (videoRef.current) {
                        videoRef.current.muted = !videoRef.current.muted
                        setVideoMuted(!videoMuted)
                      }
                    }}
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 border-white/20 bg-black/30 hover:bg-black/50"
                    title={videoMuted ? t("waitingLobby.unmuteVideo") : t("waitingLobby.muteVideo")}
                  >
                    {videoMuted ? (
                      <VolumeX className="w-4 h-4 text-white" />
                    ) : (
                      <Volume2 className="w-4 h-4 text-white" />
                    )}
                  </Button>
                  <Button
                    onClick={() => {
                      if (videoRef.current) {
                        if (videoRef.current.paused) {
                          videoRef.current.play()
                        } else {
                          videoRef.current.pause()
                        }
                      }
                    }}
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 border-white/20 bg-black/30 hover:bg-black/50"
                    title={t("waitingLobby.playPauseVideo")}
                  >
                    <Video className="w-4 h-4 text-white" />
                  </Button>
                </div>
              </div>

              {/* User info overlay */}
              <div className="absolute top-4 left-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold shadow-lg">
                  {getInitials(userName)}
                </div>
                <div>
                  <p className="text-white font-medium text-sm">{userName}</p>
                  <p className="text-white/70 text-xs">{t("waitingLobby.waitingRoom")}</p>
                </div>
              </div>
            </div>

            {/* Meeting Info Card */}
            {/* <div className="bg-card rounded-2xl shadow-lg px-6 py-4 border border-slate-200 dark:border-border">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-foreground/80 mb-3">Meeting Details</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600 dark:text-muted-foreground">Meeting Code:</span>
                  <span className="text-sm font-mono font-semibold text-slate-900 dark:text-foreground/80">{meetingCode}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600 dark:text-muted-foreground">Status:</span>
                  <span className="text-sm font-medium text-amber-600">Waiting for host</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600 dark:text-muted-foreground">Your audio:</span>
                  <span className="text-sm font-medium text-slate-900 dark:text-foreground/80">
                    {micEnabled ? "Enabled" : "Muted"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600 dark:text-muted-foreground">Your video:</span>
                  <span className="text-sm font-medium text-slate-900 dark:text-foreground/80">
                    {videoEnabled ? "On" : "Off"}
                  </span>
                </div>
              </div>
            </div> */}
          </div>

          {/* Right: Waiting Status & Controls */}
          <div className="flex flex-col justify-center items-center space-y-6 px-6">
            {/* User Avatar */}
            <div className="relative">
              <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-2xl">
                <span className="text-3xl sm:text-4xl font-bold text-white">
                  {getInitials(userName)}
                </span>
              </div>
              {/* Pulse animation ring */}
              <div className="absolute inset-0 rounded-full bg-indigo-400 animate-ping opacity-20"></div>
            </div>

            {/* User Info */}
            <div className="text-center space-y-2">
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
                {userName}
              </h1>
              <p className="text-muted-foreground">
                {t("waitingLobby.waitingToBeAdmitted")}
              </p>
            </div>

            {/* Waiting Status */}
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
              <div className="space-y-2">
                <h2 className="text-xl sm:text-2xl font-semibold text-foreground">
                  {t("waitingLobby.waitingForHost")}
                </h2>
                <p className="text-muted-foreground max-w-md mx-auto">
                  {t("waitingLobby.pleaseWait")}
                </p>
              </div>
            </div>

            {/* Status Indicator */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-muted border border-border rounded-full">
              <div className="w-2 h-2 bg-warning-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-foreground">{t("waitingLobby.inWaitingRoom")}</span>
            </div>
          </div>
        </div>
      </main>

      {/* Sticky Footer Controls */}
      <footer className="sticky bottom-0 backdrop-blur-sm border-t border-border px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-center">
          {/* Left side info */}
    

          {/* Center controls */}
          <div className="flex items-center justify-center gap-4">
            {/* Microphone Toggle */}
            <Button
              onClick={() => setMicEnabled(!micEnabled)}
              size="icon"
              className={`group relative w-12 h-12 rounded-full transition-all duration-300 shadow-lg ${
                micEnabled
                  ? "bg-secondary hover:bg-secondary/80 text-white"
                  : "bg-destructive hover:bg-destructive/80 text-white"
              }`}
              title={micEnabled ? t("waitingLobby.muteMicrophone") : t("waitingLobby.unmuteMicrophone")}
            >
              {micEnabled ? (
                <Mic className="w-5 h-5 text-white" />
              ) : (
                <MicOff className="w-5 h-5 text-white" />
              )}
            </Button>

            {/* Video Toggle */}
            <Button
              onClick={() => setVideoEnabled(!videoEnabled)}
              size="icon"
              className={`group relative w-12 h-12 rounded-full transition-all duration-300 shadow-lg ${
                videoEnabled
                  ? "bg-secondary hover:bg-secondary/80 text-white"
                  : "bg-destructive hover:bg-destructive/80 text-white"
              }`}
              title={videoEnabled ? t("waitingLobby.turnOffCamera") : t("waitingLobby.turnOnCamera")}
            >
              {videoEnabled ? (
                <Video className="w-5 h-5 text-white" />
              ) : (
                <VideoOff className="w-5 h-5 text-white" />
              )}
            </Button>

            {/* Leave Button */}
            <Button
              onClick={onBack}
              size="icon"
              variant="destructive"
              className="group relative w-12 h-12 rounded-full transition-all duration-300 shadow-lg"
              title={t("waitingLobby.leaveWaitingRoom")}
            >
              <Phone className="w-5 h-5 text-white transform rotate-135" />
            </Button>
          </div>
        </div>
      </footer>
    </div>
  )
}
